import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter } from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { brand } from "@/design-system/tokens";

const FOOTER_LINKS: Array<{
    title: string;
    links: Array<{ label: string; to: string }>;
}> = [
    {
        title: "Company",
        links: [
            { label: "About", to: "/about" },
            { label: "Careers", to: "/careers" },
            { label: "Press", to: "/press" },
        ],
    },
    {
        title: "Support",
        links: [
            { label: "Help Center", to: "/help" },
            { label: "Contact", to: "/contact" },
            { label: "Terms", to: "/terms" },
            { label: "Privacy", to: "/privacy" },
        ],
    },
    {
        title: "Explore",
        links: [
            { label: "Categories", to: ROUTES.categories },
            { label: "Orders", to: ROUTES.orders },
            { label: "Cart", to: ROUTES.cart },
        ],
    },
];

const SOCIAL: Array<{ label: string; icon: React.ComponentType<{ className?: string }>; href: string }> = [
    { label: "Instagram", icon: Instagram, href: "https://www.instagram.com/tsgo.official" },
    { label: "Twitter", icon: Twitter, href: "#" },
    { label: "Facebook", icon: Facebook, href: "#" },
];

export function Footer() {
    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="footer"
            className="mt-16 hidden border-t border-border/70 bg-muted/40 md:block"
        >
            <div className="tsgo-container py-12">
                <div className="grid gap-10 md:grid-cols-4">
                    {/* Brand block */}
                    <div className="md:col-span-1">
                        <Link
                            to={ROUTES.home}
                            className="flex items-center gap-2"
                            data-testid="footer-brand"
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand text-white shadow-brand">
                                <span className="text-xl font-extrabold leading-none">
                                    T
                                </span>
                            </span>
                            <span className="text-2xl font-extrabold tracking-tight">
                                {brand.name}
                            </span>
                        </Link>
                        <p className="mt-4 max-w-xs text-sm text-muted-foreground">
                            {brand.tagline}
                        </p>
                        <div className="mt-6 flex items-center gap-2">
                            {SOCIAL.map(({ label, icon: Icon, href }) => {
                                const external = href.startsWith("http");
                                return (
                                    <a
                                        key={label}
                                        href={href}
                                        aria-label={label}
                                        target={external ? "_blank" : undefined}
                                        rel={external ? "noreferrer" : undefined}
                                        data-testid={`footer-social-${label.toLowerCase()}`}
                                        className="grid h-9 w-9 place-items-center rounded-full border border-border/70 bg-background text-foreground/70 transition hover:border-brand hover:text-brand"
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Link columns */}
                    {FOOTER_LINKS.map((section) => (
                        <div key={section.title}>
                            <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground/80">
                                {section.title}
                            </h4>
                            <ul className="mt-4 space-y-2">
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <Link
                                            to={link.to}
                                            className="text-sm text-muted-foreground transition hover:text-brand"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-border/70 pt-6 text-xs text-muted-foreground md:flex-row md:items-center">
                    <p>© {year} TSGo. All rights reserved.</p>
                    <p className="font-medium">
                        Delivered with care — usually in minutes.
                    </p>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
